import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { createAppTheme, readStoredThemeMode, THEME_STORAGE_KEY } from "./appTheme";

const ThemeModeContext = createContext({
    mode: "light",
    toggleTheme: () => {},
    setThemeMode: () => {},
});

export function useThemeMode() {
    return useContext(ThemeModeContext);
}

export default function ThemeModeProvider({ children }) {
    const [mode, setMode] = useState(readStoredThemeMode);

    const theme = useMemo(() => createAppTheme(mode), [mode]);

    useEffect(() => {
        localStorage.setItem(THEME_STORAGE_KEY, mode);
        document.documentElement.setAttribute("data-theme", mode);
        document.documentElement.style.colorScheme = mode;
    }, [mode]);

    const toggleTheme = useCallback(() => {
        setMode((prev) => (prev === "light" ? "dark" : "light"));
    }, []);

    const setThemeMode = useCallback((next) => {
        setMode(next === "dark" ? "dark" : "light");
    }, []);

    const value = useMemo(
        () => ({ mode, toggleTheme, setThemeMode }),
        [mode, toggleTheme, setThemeMode],
    );

    return (
        <ThemeModeContext.Provider value={value}>
            <ThemeProvider theme={theme}>
                <CssBaseline enableColorScheme />
                {children}
            </ThemeProvider>
        </ThemeModeContext.Provider>
    );
}
